/**
 * Every row of a paged search, for figures that must count all of them (a
 * dashboard total read from one 200-row page undercounted any organisation
 * with more).
 *
 * The first page gives the total; the rest are fetched a few at a time, so a
 * large organisation does not open dozens of requests at once. `maxRows` is a
 * safety ceiling, not an expected size.
 */
export async function fetchAllPages<T>(
  fetchPage: (
    pageIndex: number,
    pageSize: number,
  ) => Promise<{ rows: T[]; total: number }>,
  { pageSize = 200, maxRows = 5000, concurrency = 3 } = {},
): Promise<T[]> {
  const first = await fetchPage(0, pageSize);
  const total = Math.min(first.total || first.rows.length, maxRows);
  const pages = Math.ceil(total / pageSize);
  if (pages <= 1) return first.rows;

  const rest: T[][] = new Array(pages - 1);
  let next = 1;
  const worker = async () => {
    while (next < pages) {
      const index = next++;
      rest[index - 1] = (await fetchPage(index, pageSize)).rows;
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(concurrency, pages - 1) }, worker),
  );
  return [...first.rows, ...rest.flat()];
}
